import { useState, useEffect, useMemo, useRef } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { z } from "zod";
import { Plus, Check, CalendarIcon, AlertCircle, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parse, isValid } from "date-fns";
import { parseLocalDate } from "@/lib/dates";

const DEFAULT_INCOME_CATEGORIES = ["Salary", "Freelance", "Investment", "Gift", "Other"];
const DEFAULT_EXPENSE_CATEGORIES = [
  "Housing",
  "Transportation",
  "Food",
  "Utilities",
  "Healthcare",
  "Entertainment",
  "Shopping",
  "Education",
  "Savings",
  "Other",
];

const transactionSchema = z.object({
  type: z.enum(["income", "expense"]),
  amount: z.number().positive({ message: "Amount must be greater than 0" }).max(1000000000, { message: "Amount too large" }),
  category: z.string().trim().min(1, { message: "Category is required" }).max(100),
  account: z.string().trim().min(1, { message: "Account is required" }).max(100),
  description: z.string().max(500, { message: "Description must be less than 500 characters" }).optional().nullable(),
  date: z.string().min(1, { message: "Date is required" }),
});

interface Transaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  category: string;
  account?: string | null;
  account_id?: string | null;
  description: string | null;
  date: string;
}

interface TransactionEditDialogProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const TransactionEditDialog = ({
  transaction,
  open,
  onOpenChange,
  onSuccess,
}: TransactionEditDialogProps) => {
  const [type, setType] = useState<"income" | "expense">("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [customCategories, setCustomCategories] = useState<{ income: string[]; expense: string[] }>({ income: [], expense: [] });
  const [showNewCategoryConfirm, setShowNewCategoryConfirm] = useState(false);
  const [pendingCategoryName, setPendingCategoryName] = useState("");
  const [account, setAccount] = useState("");
  const [accountFocused, setAccountFocused] = useState(false);
  const [description, setDescription] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [date, setDate] = useState("");
  const [dateCalendarOpen, setDateCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentSources, setPaymentSources] = useState<{ value: string; label: string; isWallet?: boolean; parentAccount?: string; isFundingOption?: boolean }[]>([]);
  const [fundingSourceOptions, setFundingSourceOptions] = useState<Map<string, string>>(new Map());
  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, preferences } = useUserPreferences();

  const categoryInputRef = useRef<HTMLInputElement>(null);
  const accountInputRef = useRef<HTMLInputElement>(null);

  // Get locale-based date format
  const getDateFormat = () => {
    const locale = preferences?.locale || "en-US";
    if (locale.startsWith("en-US")) return "MM/dd/yyyy";
    if (locale.startsWith("en-GB") || locale.startsWith("de") || locale.startsWith("fr") || locale.startsWith("es") || locale.startsWith("it")) return "dd/MM/yyyy";
    if (locale.startsWith("ja") || locale.startsWith("zh") || locale.startsWith("ko")) return "yyyy/MM/dd";
    return "dd/MM/yyyy";
  };

  const dateFormat = getDateFormat();

  const parseDateInput = (input: string): string | null => {
    if (!input.trim()) return null;
    try {
      const parsed = parse(input, dateFormat, new Date());
      if (isValid(parsed)) {
        return format(parsed, "yyyy-MM-dd");
      }
    } catch {
      try {
        const parsed = new Date(input);
        if (isValid(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  const formatDateForDisplay = (isoDate: string): string => {
    try {
      const d = parseLocalDate(isoDate);
      if (isValid(d)) {
        return format(d, dateFormat);
      }
    } catch {
      // invalid date — fall back to the raw ISO string
    }
    return isoDate;
  };

  // Merge default and custom categories
  const allCategories = useMemo(() => {
    const defaults = type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    const custom = type === "income" ? customCategories.income : customCategories.expense;
    const merged = [...new Set([...defaults, ...custom])];
    return merged.sort((a, b) => a.localeCompare(b));
  }, [type, customCategories]);

  const filteredCategories = useMemo(() => {
    if (!category.trim()) return allCategories;
    return allCategories.filter(cat => 
      cat.toLowerCase().includes(category.toLowerCase())
    );
  }, [allCategories, category]);

  const isNewCategory = useMemo(() => {
    if (!category.trim()) return false;
    return !allCategories.some(cat => cat.toLowerCase() === category.toLowerCase());
  }, [allCategories, category]);

  const isCategoryValid = useMemo(() => {
    if (!category.trim()) return false;
    return allCategories.some(cat => cat.toLowerCase() === category.toLowerCase());
  }, [allCategories, category]);

  const filteredPaymentSources = useMemo(() => {
    if (!account.trim()) return paymentSources;
    const search = account.toLowerCase();
    return paymentSources.filter(source => 
      source.value.toLowerCase().includes(search) || 
      source.label.toLowerCase().includes(search)
    );
  }, [paymentSources, account]);

  const isAccountValid = useMemo(() => {
    if (!account.trim()) return false;
    return paymentSources.some(s => s.value === account);
  }, [paymentSources, account]);

  const selectAccount = (value: string) => {
    setAccount(value);
    setAccountFocused(false);
    accountInputRef.current?.blur();
  };

  const selectCategory = (cat: string) => {
    setCategory(cat);
    setCategoryFocused(false);
    categoryInputRef.current?.blur();
  };

  const promptNewCategory = () => {
    if (category.trim() && isNewCategory) {
      setPendingCategoryName(category.trim());
      setShowNewCategoryConfirm(true);
      setCategoryFocused(false);
    }
  };

  const confirmAddNewCategory = async () => {
    if (!pendingCategoryName) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("categories").insert({
          user_id: user.id,
          name: pendingCategoryName,
          type: type,
        });
        
        setCustomCategories(prev => ({
          ...prev,
          [type]: [...prev[type], pendingCategoryName]
        }));
        
        setCategory(pendingCategoryName);
        toast({
          title: "Category Added",
          description: `"${pendingCategoryName}" added to ${type} categories`,
        });
      }
    } catch (error) {
      console.log("Category may already exist:", error);
    }
    
    setShowNewCategoryConfirm(false);
    setPendingCategoryName("");
    categoryInputRef.current?.blur();
  };

  useEffect(() => {
    if (transaction) {
      setType(transaction.type);
      setAmount(transaction.amount.toString());
      setCategory(transaction.category);
      setAccount(transaction.account || "");
      setDescription(transaction.description || "");
      setDate(transaction.date);
      setDateInput("");
    }
  }, [transaction]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [accountsRes, walletsRes, categoriesRes, transactionsRes, fundingSourcesRes] = await Promise.all([
          supabase.from("accounts").select("id, name").order("created_at", { ascending: true }),
          supabase.from("wallets").select("id, account_id, name").order("created_at", { ascending: true }),
          supabase.from("categories").select("name, type"),
          supabase.from("transactions").select("type, category"),
          supabase.from("account_funding_sources").select("*").order('account_id').order('priority'),
        ]);

        if (accountsRes.error) throw accountsRes.error;

        const accounts = accountsRes.data || [];
        const wallets = walletsRes.data || [];
        const fundingSources = fundingSourcesRes.data || [];
        
        // Build funding source map
        const fundingOptions = new Map<string, string>();
        
        // Build payment sources list
        const sources: { value: string; label: string; isWallet?: boolean; parentAccount?: string; isFundingOption?: boolean }[] = [];
        
        accounts.forEach(acc => {
          sources.push({ value: acc.name, label: acc.name });

          // A pass-through option per ranked funding source. These live in the
          // account_funding_sources table (account_id -> funding_account_id, by
          // priority); the old single accounts.funding_source_id column was
          // removed, which had silently disabled this whole feature here.
          const accFundingSources = fundingSources
            .filter(fs => fs.account_id === acc.id)
            .sort((a, b) => a.priority - b.priority);
          accFundingSources.forEach(fs => {
            const fundingAccount = accounts.find(a => a.id === fs.funding_account_id);
            if (fundingAccount) {
              const passThruValue = `${acc.name} ⇢ ${fundingAccount.name}`;
              sources.push({
                value: passThruValue,
                label: fs.label || `${acc.name} → ${fundingAccount.name}`,
                isFundingOption: true,
                parentAccount: fundingAccount.name,
              });
              fundingOptions.set(passThruValue, fundingAccount.name);
            }
          });

          const accountWallets = wallets.filter(w => w.account_id === acc.id);
          accountWallets.forEach(wallet => {
            sources.push({
              value: `${acc.name} → ${wallet.name}`,
              label: `${wallet.name}`,
              isWallet: true,
              parentAccount: acc.name
            });
          });
        });

        setPaymentSources(sources);
        setFundingSourceOptions(fundingOptions);

        // For new transactions that only have account_id (no legacy account text),
        // resolve the account name now that we have the accounts list
        if (transaction?.account_id && !transaction.account) {
          const matchedAccount = accounts.find(a => a.id === transaction.account_id);
          if (matchedAccount) {
            setAccount(matchedAccount.name);
          }
        }

        // Combine categories from categories table and transactions (for backward compat)
        const incomeCategoriesFromDb = new Set<string>();
        const expenseCategoriesFromDb = new Set<string>();
        
        // Add from categories table
        if (categoriesRes.data) {
          categoriesRes.data.forEach(c => {
            if (c.type === "income") {
              incomeCategoriesFromDb.add(c.name);
            } else {
              expenseCategoriesFromDb.add(c.name);
            }
          });
        }
        
        // Add from existing transactions (for backward compatibility)
        if (transactionsRes.data) {
          transactionsRes.data.forEach(t => {
            if (t.type === "income" && t.category) {
              incomeCategoriesFromDb.add(t.category);
            } else if (t.type === "expense" && t.category) {
              expenseCategoriesFromDb.add(t.category);
            }
          });
        }
        
        setCustomCategories({ 
          income: [...incomeCategoriesFromDb], 
          expense: [...expenseCategoriesFromDb] 
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction) return;

    setLoading(true);

    try {
      if (!isAccountValid) {
        throw new Error("Please select a valid account");
      }

      const validatedData = transactionSchema.parse({
        type,
        amount: parseNumber ? parseNumber(amount) : parseFloat(amount),
        category,
        account,
        description: description.trim() || null,
        date,
      });

      // Handle funding source pass-through
      let actualAccount = validatedData.account;
      const fundingTarget = fundingSourceOptions.get(validatedData.account);
      if (fundingTarget) {
        actualAccount = fundingTarget;
      }

      const { error } = await supabase
        .from("transactions")
        .update({
          type: validatedData.type,
          amount: validatedData.amount,
          category: validatedData.category,
          account: actualAccount,
          description: validatedData.description,
          date: validatedData.date,
        })
        .eq("id", transaction.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Transaction updated successfully",
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Transaction</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v: "income" | "expense") => setType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-amount">Amount</Label>
            <Input
              id="edit-amount"
              type="text"
              inputMode="decimal"
              placeholder={getPlaceholder ? getPlaceholder() : "0.00"}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use "{decimalSeparator || '.'}" for decimals
            </p>
          </div>

          {/* Category - Input with dropdown */}
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={categoryInputRef}
                  placeholder="Type to search or add..."
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  onFocus={() => setCategoryFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setCategoryFocused(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredCategories.length > 0) {
                      e.preventDefault();
                      selectCategory(filteredCategories[0]);
                    } else if (e.key === "Escape") {
                      setCategoryFocused(false);
                      categoryInputRef.current?.blur();
                    }
                  }}
                  className={cn(
                    "pr-8",
                    category && !isCategoryValid && "border-destructive"
                  )}
                />
                {category && isCategoryValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {category && !isCategoryValid && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setCategoryFocused(!categoryFocused);
                  if (!categoryFocused) {
                    categoryInputRef.current?.focus();
                  }
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {categoryFocused && (filteredCategories.length > 0 || isNewCategory) && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[200px] overflow-y-auto">
                {isNewCategory && category.trim() && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => promptNewCategory()}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left text-primary-text"
                  >
                    <Plus className="h-4 w-4" />
                    Add "{category.trim()}" as new category
                  </button>
                )}
                {filteredCategories.slice(0, 8).map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectCategory(cat)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      category === cat && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4", category === cat ? "opacity-100" : "opacity-0")} />
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Account - Input with dropdown */}
          <div className="space-y-2">
            <Label>
              {type === "income" ? "Income Source / Account" : "Payment Account"}
            </Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={accountInputRef}
                  placeholder="Type to search accounts..."
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  onFocus={() => setAccountFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setAccountFocused(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredPaymentSources.length > 0) {
                      e.preventDefault();
                      selectAccount(filteredPaymentSources[0].value);
                    } else if (e.key === "Escape") {
                      setAccountFocused(false);
                      accountInputRef.current?.blur();
                    }
                  }}
                  className={cn(
                    "pr-8",
                    account && !isAccountValid && "border-destructive"
                  )}
                />
                {account && isAccountValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {account && !isAccountValid && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setAccountFocused(!accountFocused);
                  if (!accountFocused) {
                    accountInputRef.current?.focus();
                  }
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {accountFocused && filteredPaymentSources.length > 0 && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[250px] overflow-y-auto">
                {filteredPaymentSources.slice(0, 10).map((source) => (
                  <button
                    key={source.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectAccount(source.value)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      source.isWallet && "pl-6",
                      source.isFundingOption && "pl-4 text-muted-foreground",
                      account === source.value && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4 shrink-0", account === source.value ? "opacity-100" : "opacity-0")} />
                    {source.isWallet ? (
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground text-xs">└</span>
                        {source.label}
                      </span>
                    ) : source.isFundingOption ? (
                      <span className="flex items-center gap-1 text-sm">
                        <span className="text-xs">↳</span>
                        {source.label}
                      </span>
                    ) : (
                      <span className="font-medium">{source.label}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            {!isAccountValid && account && !accountFocused && (
              <p className="text-xs text-destructive">Please select a valid account</p>
            )}
            {fundingSourceOptions.get(account) && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="text-primary-text">↳</span> 
                Will be recorded on <span className="font-medium">{fundingSourceOptions.get(account)}</span>
              </p>
            )}
          </div>

          {/* Date - Input with calendar picker */}
          <div className="space-y-2">
            <Label>Date</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder={dateFormat.toLowerCase()}
                  value={dateInput || formatDateForDisplay(date)}
                  onChange={(e) => {
                    setDateInput(e.target.value);
                    const parsed = parseDateInput(e.target.value);
                    if (parsed) {
                      setDate(parsed);
                    }
                  }}
                  onBlur={() => {
                    const parsed = parseDateInput(dateInput);
                    if (parsed) {
                      setDate(parsed);
                      setDateInput("");
                    } else if (dateInput) {
                      setDateInput("");
                    }
                  }}
                  className={cn(
                    dateInput && !parseDateInput(dateInput) && "border-destructive"
                  )}
                />
              </div>
              <Popover open={dateCalendarOpen} onOpenChange={setDateCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" type="button">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={date ? parseLocalDate(date) : undefined}
                    onSelect={(d) => {
                      if (d) {
                        setDate(format(d, "yyyy-MM-dd"));
                        setDateInput("");
                        setDateCalendarOpen(false);
                      }
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-xs text-muted-foreground">
              Format: {dateFormat.toLowerCase()}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-description">Description (Optional)</Label>
            <Textarea
              id="edit-description"
              placeholder="Add a note..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/500 characters
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
      
      {/* Category confirmation dialog */}
      <AlertDialog open={showNewCategoryConfirm} onOpenChange={setShowNewCategoryConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create new category?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingCategoryName}" doesn't exist yet. Would you like to add it as a new {type} category?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowNewCategoryConfirm(false);
              setPendingCategoryName("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAddNewCategory}>
              Add Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
